import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Send, MessageSquare, Paperclip, Download } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import MentionInput from "@/components/MentionInput";

const LEGAL = new Set(["contract_analyst","attorney","legal_manager","general_counsel"]);

/**
 * Compact side-panel modal for reading + posting notes on a renewal item.
 * Reuses the existing /api/contracts/{cid}/notes GET + POST endpoints.
 * Supports @-mentions (fires bell notifications) and file attachments.
 */
export default function RenewalNotesModal({ open, contract, onClose, onChanged }) {
  const { user } = useAuth();
  const [notes, setNotes] = useState(null);
  const [team, setTeam] = useState([]);
  const [body, setBody] = useState("");
  const [file, setFile] = useState(null);
  const [mentions, setMentions] = useState([]);
  const [vis, setVis] = useState(() => {
    if (LEGAL.has(user?.role)) return "internal";
    if (user?.role === "compliance") return "compliance";
    return "shared";
  });
  const [saving, setSaving] = useState(false);
  const [resetTick, setResetTick] = useState(0);

  const load = async () => {
    if (!contract?.contract_id) return;
    try {
      const r = await api.get(`/contracts/${contract.contract_id}/notes`);
      setNotes(r.data || []);
    } catch {
      setNotes([]);
    }
  };

  useEffect(() => {
    if (!open) return;
    setNotes(null); setBody(""); setFile(null); setMentions([]);
    load();
    api.get("/users/team").then(r => setTeam(r.data || [])).catch(() => {});
  }, [open, contract?.contract_id]);

  if (!open || !contract) return null;

  const visOptions = LEGAL.has(user?.role)
    ? [["internal", "Internal (Legal only)"], ["compliance", "Legal + Compliance"], ["shared", "Shared (everyone with access)"]]
    : user?.role === "compliance"
      ? [["compliance", "Legal + Compliance"], ["shared", "Shared (everyone)"]]
      : [["shared", "Shared (everyone)"]];

  const submit = async () => {
    if (!body.trim() && !file) return toast.error("Add a note or attach a file");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("body", body.trim());
      fd.append("visibility", vis);
      if (mentions.length) fd.append("mentions", mentions.map(m => m.id).join(","));
      if (file) fd.append("file", file);
      await api.post(`/contracts/${contract.contract_id}/notes`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(mentions.length ? `Note added · ${mentions.length} notified` : "Note added");
      setBody(""); setFile(null); setMentions([]); setResetTick(t => t + 1);
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add note");
    } finally { setSaving(false); }
  };

  const downloadAttachment = async (noteId, idx, filename) => {
    try {
      const r = await api.get(`/contracts/${contract.contract_id}/notes/${noteId}/attachments/${idx}`,
                              { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end" data-testid="renewal-notes-modal">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white h-full flex flex-col shadow-2xl">
        <div className="px-5 py-4 border-b border-slate-200 flex items-start gap-3">
          <MessageSquare className="w-5 h-5 text-[#005289] mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Renewal Notes</div>
            <div className="font-semibold text-slate-900 truncate">{contract.vendor}</div>
            <div className="text-xs text-slate-500 font-mono">#{contract.contract_number}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="renewal-notes-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-slate-50">
          {notes === null && <div className="text-sm text-slate-500">Loading…</div>}
          {notes && notes.length === 0 && (
            <div className="text-xs text-slate-500 border border-dashed border-slate-200 rounded p-4 text-center bg-white">
              No notes yet. Start the conversation below.
            </div>
          )}
          {notes && notes.map(n => (
            <div key={n.id} className="bg-white border border-slate-200 rounded-md p-3 text-xs" data-testid={`renewal-note-${n.id}`}>
              <div className="flex items-center gap-2 mb-1">
                <div className="font-semibold text-slate-800 text-xs">{n.author_name}</div>
                <div className="text-[10px] text-slate-400">{(n.author_role || "").replace(/_/g, " ")}</div>
                <div className="ml-auto text-[10px] text-slate-400">{new Date(n.created_at).toLocaleString()}</div>
              </div>
              <div className="text-slate-700 whitespace-pre-wrap">{n.body}</div>
              {n.attachments && n.attachments.length > 0 && (
                <div className="mt-2 space-y-1">
                  {n.attachments.map((a, i) => (
                    <button key={i} onClick={() => downloadAttachment(n.id, i, a.filename)}
                            className="inline-flex items-center gap-1 text-[10px] text-[#005289] hover:underline"
                            data-testid={`renewal-note-attach-${n.id}-${i}`}>
                      <Download className="w-3 h-3" /> {a.filename}
                    </button>
                  ))}
                </div>
              )}
              <div className="mt-1">
                <span className={`inline-block text-[9px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wider ${
                  n.visibility === "internal" ? "bg-slate-100 text-slate-600" :
                  n.visibility === "compliance" ? "bg-purple-50 text-purple-700" :
                  "bg-emerald-50 text-emerald-700"
                }`}>{n.visibility}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-slate-200 p-4 bg-white">
          <MentionInput
            value={body}
            onChange={setBody}
            team={team}
            onMentionsChange={setMentions}
            resetSignal={resetTick}
            placeholder="Add a note or message on this renewal… Type @ to tag a teammate."
            testIdPrefix="renewal-note-body"
            rows={3}
          />
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <label className="inline-flex items-center gap-1 text-[11px] text-slate-600 cursor-pointer border border-slate-200 rounded px-2 py-1 hover:bg-slate-50"
                   data-testid="renewal-note-attach-label">
              <Paperclip className="w-3 h-3" /> {file ? file.name.slice(0, 20) : "Attach"}
              <input type="file" className="hidden" onChange={e => setFile(e.target.files[0] || null)}
                     data-testid="renewal-note-file" />
            </label>
            {visOptions.length > 1 ? (
              <select value={vis} onChange={e => setVis(e.target.value)}
                      className="text-xs border border-slate-200 rounded h-8 px-2 flex-1 min-w-[140px]"
                      data-testid="renewal-note-visibility">
                {visOptions.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
              </select>
            ) : <div className="flex-1 text-xs text-slate-500">Posted as <b>shared</b></div>}
            <button onClick={submit} disabled={saving || (!body.trim() && !file)}
                    className="h-8 px-3 rounded bg-[#005289] text-white text-xs font-semibold flex items-center gap-1 disabled:opacity-50"
                    data-testid="renewal-note-submit">
              <Send className="w-3 h-3" /> {saving ? "Saving…" : "Post"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
